/**
 * Item Master Types
 * Types for inventory items and item categories
 */

// ============================================
// Item Category Types
// ============================================

export interface ItemCategory {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  parent_category_id?: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  // Joined fields
  parent_category?: ItemCategory | null;
  children?: ItemCategory[];
}

export interface ItemCategoryFormData {
  code: string;
  name: string;
  description?: string;
  parent_category_id?: string | null;
  is_active?: boolean;
  sort_order?: number;
}

// ============================================
// Item Types
// ============================================

export type ItemType = 
  | 'raw_material' 
  | 'finished_good' 
  | 'semi_finished' 
  | 'consumable' 
  | 'spare_part' 
  | 'packaging';

export type MakeOrBuy = 'make' | 'buy';

export type ItemStatus = 
  | 'in_stock' 
  | 'low_stock' 
  | 'out_of_stock' 
  | 'discontinued' 
  | 'active' 
  | 'inactive';

export type StockStatus = 'in_stock' | 'low_stock' | 'out_of_stock';

export interface ItemSpecifications {
  material?: string;
  dimensions?: string;
  weight?: number;
  weight_unit?: string;
  color?: string;
  grade?: string;
  finish?: string;
  [key: string]: unknown;
}

export interface InventoryItem {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  category?: string | null; // Legacy field
  category_id?: string | null;
  item_type: ItemType;
  make_or_buy: MakeOrBuy;
  unit: string;
  uom_id?: string | null;
  stock_level: number;
  min_stock_level: number;
  reorder_level: number;
  max_stock_level?: number | null;
  lead_time_days: number;
  unit_cost: number;
  default_warehouse_id?: string | null;
  location_id?: string | null;
  supplier_id?: string | null;
  barcode?: string | null;
  specifications?: ItemSpecifications | null;
  status: ItemStatus;
  is_active: boolean;
  bom_component_id?: string | null;
  last_received?: string | null;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
}

// Extended item with joined data from view
export interface InventoryItemWithDetails extends InventoryItem {
  category_code?: string | null;
  category_name?: string | null;
  parent_category_name?: string | null;
  uom_name?: string | null;
  uom_code?: string | null;
  warehouse_name?: string | null;
  warehouse_code?: string | null;
  supplier_name?: string | null;
  stock_status: StockStatus;
}

export interface InventoryItemFormData {
  code?: string; // Optional - can be auto-generated
  name: string;
  description?: string;
  category_id?: string | null;
  item_type: ItemType;
  make_or_buy: MakeOrBuy;
  unit: string;
  uom_id?: string | null;
  stock_level?: number;
  min_stock_level?: number;
  reorder_level?: number;
  max_stock_level?: number | null;
  lead_time_days?: number;
  unit_cost?: number;
  default_warehouse_id?: string | null;
  supplier_id?: string | null;
  barcode?: string;
  specifications?: ItemSpecifications;
  status?: ItemStatus;
  is_active?: boolean;
}

// ============================================
// Filter Types
// ============================================

export interface ItemFilters {
  search?: string;
  category_id?: string;
  item_type?: ItemType;
  make_or_buy?: MakeOrBuy;
  status?: ItemStatus;
  stock_status?: StockStatus;
  is_active?: boolean;
  warehouse_id?: string;
  supplier_id?: string;
}

export interface ItemSortOptions {
  field: 'code' | 'name' | 'stock_level' | 'unit_cost' | 'created_at' | 'updated_at';
  direction: 'asc' | 'desc';
}

// ============================================
// API Response Types
// ============================================

export interface ItemMasterStats {
  total_items: number;
  active_items: number;
  low_stock_items: number;
  out_of_stock_items: number;
  total_value: number;
  items_by_category: Record<string, number>;
  items_by_type: Record<ItemType, number>;
}

export interface ItemImportData {
  code: string;
  name: string;
  description?: string;
  category_code?: string;
  item_type?: string;
  make_or_buy?: string;
  unit: string;
  stock_level?: number;
  min_stock_level?: number;
  reorder_level?: number;
  max_stock_level?: number;
  lead_time_days?: number;
  unit_cost?: number;
  barcode?: string;
}

export interface ItemExportData extends InventoryItemWithDetails {
  // All fields from InventoryItemWithDetails are included
}

// ============================================
// Hook Return Types
// ============================================

export interface UseItemMasterReturn {
  items: InventoryItemWithDetails[];
  loading: boolean;
  error: string | null;
  stats: ItemMasterStats | null;
  fetchItems: (filters?: ItemFilters, sort?: ItemSortOptions) => Promise<void>;
  getItem: (id: string) => Promise<InventoryItemWithDetails | null>;
  createItem: (data: InventoryItemFormData) => Promise<InventoryItem | null>;
  updateItem: (id: string, data: Partial<InventoryItemFormData>) => Promise<InventoryItem | null>;
  deleteItem: (id: string) => Promise<boolean>;
  generateItemCode: (categoryCode?: string) => Promise<string>;
  fetchStats: () => Promise<ItemMasterStats | null>;
  importItems: (items: ItemImportData[]) => Promise<{ success: number; failed: number; errors: string[] }>;
  exportItems: (filters?: ItemFilters) => Promise<ItemExportData[]>;
}

export interface UseItemCategoriesReturn {
  categories: ItemCategory[];
  flatCategories: ItemCategory[];
  loading: boolean;
  error: string | null;
  fetchCategories: () => Promise<void>;
  getCategory: (id: string) => Promise<ItemCategory | null>;
  createCategory: (data: ItemCategoryFormData) => Promise<ItemCategory | null>;
  updateCategory: (id: string, data: Partial<ItemCategoryFormData>) => Promise<ItemCategory | null>;
  deleteCategory: (id: string) => Promise<boolean>;
  getCategoryTree: () => ItemCategory[];
}

// ============================================
// Constants
// ============================================

export const ITEM_TYPE_LABELS: Record<ItemType, string> = {
  raw_material: 'Raw Material',
  finished_good: 'Finished Good',
  semi_finished: 'Semi-Finished',
  consumable: 'Consumable',
  spare_part: 'Spare Part',
  packaging: 'Packaging',
};

export const MAKE_OR_BUY_LABELS: Record<MakeOrBuy, string> = {
  make: 'Make',
  buy: 'Buy',
};

export const ITEM_STATUS_LABELS: Record<ItemStatus, string> = {
  in_stock: 'In Stock',
  low_stock: 'Low Stock',
  out_of_stock: 'Out of Stock',
  discontinued: 'Discontinued',
  active: 'Active',
  inactive: 'Inactive',
};

export const STOCK_STATUS_LABELS: Record<StockStatus, string> = {
  in_stock: 'In Stock',
  low_stock: 'Low Stock',
  out_of_stock: 'Out of Stock',
};

export const STOCK_STATUS_COLORS: Record<StockStatus, string> = {
  in_stock: 'bg-green-100 text-green-800',
  low_stock: 'bg-yellow-100 text-yellow-800',
  out_of_stock: 'bg-red-100 text-red-800',
};

export const ITEM_TYPE_OPTIONS = Object.entries(ITEM_TYPE_LABELS).map(([value, label]) => ({
  value: value as ItemType,
  label,
}));

export const MAKE_OR_BUY_OPTIONS = Object.entries(MAKE_OR_BUY_LABELS).map(([value, label]) => ({
  value: value as MakeOrBuy,
  label,
}));

export const ITEM_STATUS_OPTIONS = Object.entries(ITEM_STATUS_LABELS).map(([value, label]) => ({
  value: value as ItemStatus,
  label,
}));
